import React, { useEffect, useState, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import html2pdf from 'html2pdf.js';
import {
  Container,
  Box,
  Typography,
  Paper,
  Avatar,
  Divider,
  Button,
  CircularProgress,
  useTheme,
  useMediaQuery,
  Stepper,
  Step,
  StepLabel,
  Chip,
  Link,
  GlobalStyles,
} from '@mui/material';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import { FaCcVisa, FaCcMastercard, FaCcPaypal } from 'react-icons/fa';
import ShopperReceipt from './ShopperReceipt';
import GiftCardOrderSuccess from './GiftCardOrderSuccess';


const paymentIcons = {
  visa: <FaCcVisa size={32} />,
  mastercard: <FaCcMastercard size={32} />,
  paypal: <FaCcPaypal size={32} />,
};

export default function OrderSuccess() {
  // ───────────────────────────────────────────── theme / media
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm')); // < 600px
  const isSmallMobile = useMediaQuery('(max-width:400px)');     // ≤ 400px
  const isUltraSmall  = useMediaQuery('(max-width:350px)');     // ≤ 350px  ➜ vertical stepper

  // ───────────────────────────────────────────── routing / refs
  const { search } = useLocation();
  const orderId = new URLSearchParams(search).get('order_id');
  const receiptRef = useRef();

  // ───────────────────────────────────────────── state
  const [order, setOrder]       = useState(null);
  const [piDetails, setPi]      = useState(null);
  const [loading, setLoading]   = useState(true);

  // ───────────────────────────────────────────── GoogleRef
  const firedRef = useRef(false);


  // ───────────────────────────────────────────── fetch order + PI
  useEffect(() => {
    if (!orderId) return;
    (async () => {
      try {
        setLoading(true);
        const oRes = await fetch(`/orders/${orderId}`);
        if (!oRes.ok) throw new Error('Order fetch failed');
        const o = await oRes.json();
        setOrder(o);

        const piRes = await fetch(`/stripe/payment-intent/${o.id}`);
        if (piRes.ok) setPi(await piRes.json());
      } catch (err) {
        /* eslint-disable no-console */
        console.error(err);
      } finally {
        setLoading(false);
      }
    })();
  }, [orderId]);

  useEffect(() => {
  if (!order || firedRef.current) return;
  if (!window.gtag) return;

  // avoid duplicate fires across reloads / StrictMode
  const key = `ads_conv_${order.id}`;
  if (sessionStorage.getItem(key)) return;

  window.gtag('event', 'conversion', {
    send_to: 'AW-17474107567/4j6iCIT28YUbEK_xpoxB',
    value: order.total ?? 1.0,      // use your actual order total
    currency: 'EUR',
    transaction_id: order.id ?? '', // unique per order
  });

  firedRef.current = true;
  sessionStorage.setItem(key, '1');
}, [order]);

  // ───────────────────────────────────────────── PDF helper
  const downloadReceipt = () => {
    if (!receiptRef.current) return;
    html2pdf()
      .set({
        margin: 10,
        filename: `receipt_${order.id}.pdf`,
        image: { type: 'jpeg', quality: 0.98 },
        html2canvas: { scale: 2 },
        jsPDF: { unit: 'mm', format: 'a4', orientation: 'portrait' },
      })
      .from(receiptRef.current)
      .save();
  };

  // ───────────────────────────────────────────── loading / error
  if (loading) {
    return (
      <Container sx={{ py: 8, textAlign: 'center' }}>
        <CircularProgress size={60} thickness={4} />
        <Typography variant="h6" sx={{ mt: 2, fontWeight: 600 }}>
          Preparing your order details…
        </Typography>
      </Container>
    );
  }

  if (!order) {
    return (
      <Container sx={{ py: 8, textAlign: 'center' }}>
        <Typography variant="h6" color="error" sx={{ fontWeight: 600 }}>
          Order not found.
        </Typography>
      </Container>
    );
  }

  // ───────────────────────────────────────────── derived data
  const isDigital       = !order.shippingAddress;
  const shippingCost    = order.shippingCost   ?? 0;
  const processingFee   = order.processingFee  ?? 0;
  const weight          = order.weight         ?? 0;
  const trackingNumber  = order.trackingNumber ?? null;

  if (isDigital) {
    // gift-cards have their own success screen
    return <GiftCardOrderSuccess order={order} piDetails={piDetails} />;
  }

 const pm   = piDetails?.payment_method;
 const latestCharge = piDetails?.latest_charge || piDetails?.charges?.data?.[0];
 const pmd = latestCharge?.payment_method_details;
 const card = pm?.card || pmd?.card;
 const isPayPal = (pm?.type || pmd?.type) === 'paypal';
 const paypalEmail =
   pmd?.paypal?.payer_email || latestCharge?.billing_details?.email || order?.email;
  const steps      = ['Ordered', 'Confirmed', 'Dispatched', 'Delivered'];
  const activeStep = Math.max(0, steps.findIndex(s => s.toLowerCase() === order.status));

  // ───────────────────────────────────────────── UI
  return (
    <>
      {/* ───────────────── global tweaks (ONLY what’s strictly needed) */}
      <GlobalStyles styles={{
        body: { overflowX: isSmallMobile ? 'auto' : 'hidden' }, // allow x-scroll on ≤ 400px
        '.MuiStepLabel-label': {
          fontSize: isSmallMobile ? '0.65rem' : '0.75rem',
          whiteSpace: 'nowrap',
        },
      }} />

      <Container
        maxWidth="lg"
        sx={{
          py: isMobile ? 3 : 8,
          px: isMobile ? 1 : 0,
          backgroundColor: '#f4f4f4',
          overflowX: 'hidden',
        }}
      >
        <Paper
          elevation={2}
          sx={{
            p: isMobile ? 2 : 6,
            borderRadius: 3,
            overflow: 'hidden',
            position: 'relative',
          }}
        >
          {/* ───── header ───── */}
          <Box textAlign="center" mb={isMobile ? 2 : 4}>
            <Avatar
              sx={{
                bgcolor: '#000',
                width: isMobile ? 48 : 64,
                height: isMobile ? 48 : 64,
                mx: 'auto',
              }}
            >
              <CheckCircleOutlineIcon
                sx={{ fontSize: isMobile ? 30 : 40, color: '#fff' }}
              />
            </Avatar>
            <Typography
              variant={isMobile ? 'h6' : 'h4'}
              sx={{
                mt: 1.5,
                fontWeight: 800,
                fontSize: isSmallMobile ? '1.35rem' : undefined,
              }}
            >
              Order Confirmed
            </Typography>
            <Typography
              variant="subtitle1"
              sx={{
                mt: 0.5,
                fontWeight: 500,
                color: '#555',
                fontSize: isSmallMobile ? '0.85rem' : undefined,
                wordBreak: 'break-all',               // ← wrap long IDs
              }}
            >
              Order ID: <strong>{order.id}</strong>
            </Typography>
          </Box>

          {/* ───── main grid ───── */}
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' },
              gap: isMobile ? 2 : 4,
              width: '100%',
              '& > *': { minWidth: 0 },          // ← key line
            }}
          >
            {/* ───────── left column ───────── */}
            <Box sx={{ pt: isMobile ? 1 : 3 }}>
              {/* items */}
              <Typography
                variant="h6"
                sx={{ mb: 1, fontWeight: 700, fontSize: isSmallMobile ? '1rem' : undefined }}
              >
                Items
              </Typography>
              <Divider sx={{ mb: 1.5 }} />

              {order.items.map((item, idx) => (
                <Box
                  key={idx}
                  display="flex"
                  alignItems="center"
                  mb={2}
                  sx={{ flexWrap: isSmallMobile ? 'wrap' : 'nowrap', gap: isSmallMobile ? 1 : 0 }}
                >
                  {item.img && (
                    <Box
                      component="img"
                      src={item.img}
                      alt={item.name}
                      sx={{
                        width: isSmallMobile ? 40 : isMobile ? 55 : 70,
                        height: 'auto',
                        objectFit: 'cover',
                        borderRadius: 2,
                        mr: 1.5,
                        flexShrink: 0,
                      }}
                    />
                  )}
                  <Box flex={1} minWidth={100}>
                    <Typography fontWeight={600} sx={{ fontSize: isSmallMobile ? '0.9rem' : undefined }}>
                      {item.name}
                    </Typography>
                    <Typography variant="body2" sx={{ fontSize: isSmallMobile ? '0.8rem' : undefined }}>
                      Qty: {item.qty}
                    </Typography>
                  </Box>
                  <Typography
                    fontWeight={600}
                    sx={{ ml: isSmallMobile ? 'auto' : 0, fontSize: isSmallMobile ? '0.9rem' : undefined }}
                  >
                    €{item.subtotal.toFixed(2)}
                  </Typography>
                </Box>
              ))}

              <Divider sx={{ my: isMobile ? 2 : 3 }} />

              {/* shipping address + stepper */}
              <Paper
                elevation={0}
                sx={{ p: isMobile ? 1.5 : 3, backgroundColor: '#fff', borderRadius: 2, overflow: 'hidden' }}
              >
                <Typography
                  variant="h6"
                  sx={{ mb: 1, fontWeight: 700, fontSize: isSmallMobile ? '1rem' : undefined }}
                >
                  Shipping To
                </Typography>
                <Typography variant="body2" sx={{ fontSize: isSmallMobile ? '0.8rem' : undefined }}>
                  {order.shippingAddress.firstName} {order.shippingAddress.lastName}
                </Typography>
                <Typography
                  variant="body2"
                  sx={{ fontSize: isSmallMobile ? '0.8rem' : undefined, overflowWrap: 'anywhere' }} // ← wrap
                >
                  {order.email}
                </Typography>

                <Typography variant="body2" sx={{ fontSize: isSmallMobile ? '0.8rem' : undefined }}>
                  {order.shippingAddress.address1}, {order.shippingAddress.city}
                </Typography>
                <Typography variant="body2" sx={{ fontSize: isSmallMobile ? '0.8rem' : undefined }}>
                  {order.shippingAddress.postalCode}, {order.shippingAddress.country}
                </Typography>
                {order.contact && (
                  <Typography variant="body2" sx={{ fontSize: isSmallMobile ? '0.8rem' : undefined }}>
                    Phone: {order.contact}
                  </Typography>
                )}

                <Box sx={{ mt: 2, overflowX: 'auto', overflowY: 'hidden', pb: 1 }}>
                  <Stepper
                    activeStep={activeStep}
                    orientation={isUltraSmall ? 'vertical' : 'horizontal'}
                    alternativeLabel={!isUltraSmall}
                    sx={{
                      width: '100%',
                      '@keyframes blink': { '0%,100%': { opacity: 1 }, '50%': { opacity: 0.7 } },
                      '& .MuiStepIcon-root': { fontSize: isSmallMobile ? 18 : 22 },
                      '& .MuiStepIcon-root.Mui-active': { animation: 'blink 1.2s infinite' },
                      '& .MuiStepConnector-line, & .MuiStepIcon-root.Mui-completed, & .MuiStepIcon-root.Mui-active': {
                        color: '#000',
                        borderColor: '#000',
                      },
                    }}
                  >
                    {steps.map(label => (
                      <Step key={label}>
                        <StepLabel>{label}</StepLabel>
                      </Step>
                    ))}
                  </Stepper>
                </Box>
              </Paper>

              {/* tracking */}
              <Box
                mt={2}
                display="flex"
                alignItems="center"
                sx={{ borderTop: theme => `1px solid ${theme.palette.divider}`, pt: 2, overflow: 'hidden' }}
              >
                {!trackingNumber ? (
                  <Chip label="Tracking: Pending" color="warning" size="small" />
                ) : (
                  <Link
                    href={`https://www.dhl.com/en/express/tracking.html?AWB=${trackingNumber}`}
                    target="_blank"
                    rel="noopener"
                    underline="none"
                    display="flex"
                    alignItems="center"
                    sx={{
                      px: 1,
                      py: 0.5,
                      borderRadius: 1,
                      backgroundColor: theme => theme.palette.grey[100],
                      '&:hover': { backgroundColor: theme.palette.grey[200] },
                      width: '100%',
                      overflow: 'hidden',
                    }}
                  >
                    <Box component="img" src="https://brokepiggytoken.xyz/wp-content/uploads/2025/07/DHL-Logo.png" alt="DHL" sx={{ height: 24, mr: 1, flexShrink: 0 }} />
                    <Typography
                      variant="body2"
                      sx={{ fontWeight: 500, wordBreak: 'break-all', fontSize: isSmallMobile ? '0.75rem' : undefined }}
                    >
                      {trackingNumber}
                    </Typography>
                  </Link>
                )}
              </Box>
            </Box>

            {/* ───────── right column ───────── */}
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: isMobile ? 1.5 : 2 }}>
              {/* payment method */}
              <Paper elevation={0} sx={{ p: isMobile ? 1.5 : 3, backgroundColor: '#fff', borderRadius: 2 }}>
  <Typography variant="h6" sx={{ mb: 1, fontWeight: 700, fontSize: isSmallMobile ? '1rem' : undefined }}>
    Payment Method
  </Typography>
  {card ? (
    <Box display="flex" alignItems="center" gap={1.5}>
      {paymentIcons[card.brand?.toLowerCase?.()] || paymentIcons.visa}
      <Box>
        <Typography fontWeight={600} sx={{ fontSize: isSmallMobile ? '0.9rem' : undefined }}>
          {(card.brand || 'Card').toUpperCase()} ••••{card.last4 || '••••'}
        </Typography>
        <Typography variant="body2" sx={{ fontSize: isSmallMobile ? '0.8rem' : undefined }}>
          Expires {String(card.exp_month || '').padStart(2, '0')}/{card.exp_year || '—'}
        </Typography>
      </Box>
    </Box>
  ) : isPayPal ? (
  <Box display="flex" alignItems="center" gap={1.5}>
    {paymentIcons.paypal}
    <Box sx={{ minWidth: 0 }}> {/* allow wrapping inside */}
      <Typography
        fontWeight={600}
        sx={{
          fontSize: isSmallMobile ? '0.9rem' : undefined,
          overflowWrap: 'anywhere',       // ← wrap long emails
          wordBreak: 'break-word',
        }}
      >
        PayPal {paypalEmail ? `(${paypalEmail})` : ''}
      </Typography>
    </Box>
  </Box>
) : (
    <Typography>Payment details available after processing…</Typography>
  )}
</Paper>

              {/* summary */}
              <Paper elevation={0} sx={{ p: isMobile ? 1.5 : 3, backgroundColor: '#fff', borderRadius: 2 }}>
                <Typography variant="h6" sx={{ mb: 1, fontWeight: 700, fontSize: isSmallMobile ? '1rem' : undefined }}>
                  Summary
                </Typography>
                <Divider sx={{ mb: 1.5 }} />
                <Typography variant="body2" sx={{ fontSize: isSmallMobile ? '0.8rem' : undefined }}>
                  Weight: {weight} kg
                </Typography>
                <Typography variant="body1" fontWeight={500} sx={{ fontSize: isSmallMobile ? '0.9rem' : undefined }}>
                  Shipping: €{shippingCost.toFixed(2)}
                </Typography>
                <Typography variant="body1" sx={{ fontSize: isSmallMobile ? '0.9rem' : undefined }}>
                  Processing Fee: €{processingFee.toFixed(2)}
                </Typography>
                <Typography variant="body1" fontWeight={700} sx={{ mt: 1, fontSize: isSmallMobile ? '1rem' : undefined }}>
                  Total Paid: €{order.total.toFixed(2)}
                </Typography>
              </Paper>

              {/* actions */}
              <Box display="flex" gap={isMobile ? 1 : 2}>
                <Button
                  fullWidth
                  variant="outlined"
                  onClick={() => alert('Receipt emailed!')}
                  sx={{ fontWeight: 600, fontSize: isSmallMobile ? '0.75rem' : undefined }}
                >
                  Email
                </Button>
                <Button
                  fullWidth
                  variant="contained"
                  color="secondary"
                  onClick={downloadReceipt}
                  sx={{ fontWeight: 600, fontSize: isSmallMobile ? '0.75rem' : undefined }}
                >
                  Download PDF
                </Button>
              </Box>
            </Box>
          </Box>
        </Paper>

        {/* ───────────────── hidden template for PDF generation */}
        <div
          ref={receiptRef}
          style={{
            position: 'absolute',
            left: '-9999px',
            top: 0,
            width: '210mm',
            padding: '10mm',
            background: '#fff',
          }}
        >
          <ShopperReceipt order={order} piDetails={piDetails} />
        </div>
      </Container>
    </>
  );
}
